import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui';
import type { GroupSchedule } from '../../types/api';

const DAYS = [
  { value: 1, label: 'Пн' },
  { value: 2, label: 'Вт' },
  { value: 3, label: 'Ср' },
  { value: 4, label: 'Чт' },
  { value: 5, label: 'Пт' },
  { value: 6, label: 'Сб' },
  { value: 0, label: 'Вс' },
];

interface AddScheduleSlotSheetProps {
  onSave: (slot: GroupSchedule) => void;
  onClose: () => void;
  editSlot?: GroupSchedule | null;
}

export const AddScheduleSlotSheet: React.FC<AddScheduleSlotSheetProps> = ({
  onSave,
  onClose,
  editSlot,
}) => {
  const [dayOfWeek, setDayOfWeek] = useState<number>(editSlot?.day_of_week ?? 1);
  const [startTime, setStartTime] = useState(editSlot?.start_time ?? '18:00');
  const [endTime, setEndTime] = useState(editSlot?.end_time ?? '19:30');
  const [location, setLocation] = useState(editSlot?.location ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startTime || !endTime) return;
    onSave({
      day_of_week: dayOfWeek,
      start_time: startTime,
      end_time: endTime,
      location: location.trim() || undefined,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end"
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl flex flex-col"
        style={{ backgroundColor: 'var(--bg-primary)', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex-shrink-0 px-4 py-4 border-b"
          style={{ borderColor: 'var(--bg-secondary)' }}
        >
          <div className="flex items-center justify-between">
            <h2
              className="text-lg font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              {editSlot ? 'Редактировать занятие' : 'Добавить занятие'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg"
              style={{ color: 'var(--text-hint)' }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* Day of week */}
          <div>
            <label
              className="block text-[13px] font-medium mb-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              День недели
            </label>
            <div className="flex gap-1.5">
              {DAYS.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => setDayOfWeek(day.value)}
                  className="flex-1 py-2.5 rounded-lg text-[14px] font-medium transition-colors"
                  style={{
                    backgroundColor:
                      dayOfWeek === day.value ? 'var(--accent)' : 'var(--bg-secondary)',
                    color: dayOfWeek === day.value ? '#FFFFFF' : 'var(--text-primary)',
                  }}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Time */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label
                className="block text-[13px] font-medium mb-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                Начало
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg text-[15px]"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                }}
                required
              />
            </div>
            <div className="flex-1">
              <label
                className="block text-[13px] font-medium mb-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                Конец
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg text-[15px]"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                }}
                required
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label
              className="block text-[13px] font-medium mb-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              Место (необязательно)
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Например: Зал 3"
              maxLength={100}
              className="w-full px-3 py-2.5 rounded-lg text-[15px]"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: 'none',
              }}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 pb-4">
            <Button variant="secondary" onClick={onClose} className="flex-1">
              Отмена
            </Button>
            <Button
              variant="primary"
              disabled={!startTime || !endTime}
              className="flex-1"
            >
              {editSlot ? 'Сохранить' : 'Добавить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
